import { AppData, DeveloperData, AIInsightItem } from '../types';

// Helper to generate ranking points
function generateRankings(startRank: number, endRank: number, count: number, volatility = 3) {
  const points = [];
  const now = new Date();
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 86400000 * (30 / count));
    const progress = 1 - (i / count);
    const base = startRank - (startRank - endRank) * progress;
    const noise = (Math.sin(i * 1.5) * volatility);
    const rank = Math.max(1, Math.round(base + noise));
    points.push({
      date: d.toISOString().split('T')[0],
      rank,
      categoryRank: Math.max(1, Math.round(rank / 4)),
      overallRank: rank * 3
    });
  }
  return points;
}

export const APPS_DATA: Record<string, AppData> = {
  'duolingo': {
    id: 'duolingo',
    slug: 'duolingo',
    name: 'Duolingo: Language Lessons',
    developer: 'Duolingo, Inc.',
    developerName: 'Duolingo, Inc.',
    developerSlug: 'duolingo',
    developerId: 570060131,
    appStoreId: 570060128,
    googlePlayId: 'com.duolingo',
    bundleId: 'com.duolingo.DuolingoMobile',
    iconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/67/e4/51/67e4516e-c1d0-452b-3f3e-5def33df3138/AppIcon-0-0-1x_U007epad-0-1-85-220.png/512x512bb.jpg',
    appStoreIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/67/e4/51/67e4516e-c1d0-452b-3f3e-5def33df3138/AppIcon-0-0-1x_U007epad-0-1-85-220.png/512x512bb.jpg',
    googlePlayIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/67/e4/51/67e4516e-c1d0-452b-3f3e-5def33df3138/AppIcon-0-0-1x_U007epad-0-1-85-220.png/512x512bb.jpg',
    category: 'Education',
    categorySlug: 'education',
    stores: ['appstore', 'googleplay'],
    primaryStore: 'appstore',
    subtitle: 'Learn Spanish, French, German & more',
    description: 'Learn a new language with the world’s most-downloaded education app! Duolingo is the fun, free app for learning 40+ languages through quick, bite-sized lessons.',
    price: 'Free',
    inAppPurchases: true,
    dataSource: 'Official',
    appStoreUrl: 'https://apps.apple.com/us/app/duolingo-language-lessons/id570060128',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.duolingo',
    firstReleaseDate: '2012-06-19',
    lastUpdated: '2026-08-18',
    currentRank: 2,
    previousRank: 4,
    categoryRank: 1,
    growthScore: 96,
    growthClassification: 'Breakout',
    growthVelocityPct: 14.2,
    rating: 4.8,
    totalReviews: 2480000,
    ratingCount: 2480000,
    ratingDistribution: { star5: 1984000, star4: 310000, star3: 98000, star2: 44000, star1: 44000 },
    estimatedMonthlyDownloads: 9200000,
    estimatedMonthlyRevenueUsd: 28400000,
    estimatedGrowthMoM: 16.5,
    isEstimated: true,
    outlier: {
      isOutlier: true,
      level: 'strong',
      multiplier: 3.8,
      growthVs30dAveragePct: 214,
      normalVelocity: '+3.2% / week',
      currentVelocity: '+14.2% / week',
      detectedAt: '2026-08-22',
      summary: 'Duolingo is growing 3.8× faster than the Education category average this week.'
    },
    growthFactors: {
      rankingVelocity: 94,
      reviewGrowth: 98,
      ratingMomentum: 95,
      categoryMovement: 97,
      countryExpansion: 96,
      updateActivity: 92
    },
    rankingsHistory: {
      '24H': generateRankings(3, 2, 24, 0.4),
      '7D': generateRankings(4, 2, 7, 0.6),
      '30D': generateRankings(8, 2, 30, 1.2),
      '90D': generateRankings(14, 2, 90, 2),
      '1Y': generateRankings(26, 2, 52, 3),
      'ALL': generateRankings(45, 2, 100, 4)
    },
    countryRankings: [
      { countryCode: 'US', countryName: 'United States', flag: '🇺🇸', rank: 2, previousRank: 4, change: 2, category: 'Education #1' },
      { countryCode: 'MX', countryName: 'Mexico', flag: '🇲🇽', rank: 1, previousRank: 2, change: 1, category: 'Education #1' },
      { countryCode: 'CA', countryName: 'Canada', flag: '🇨🇦', rank: 3, previousRank: 4, change: 1, category: 'Education #1' },
      { countryCode: 'GB', countryName: 'United Kingdom', flag: '🇬🇧', rank: 2, previousRank: 3, change: 1, category: 'Education #1' },
      { countryCode: 'DE', countryName: 'Germany', flag: '🇩🇪', rank: 4, previousRank: 6, change: 2, category: 'Education #1' },
      { countryCode: 'FR', countryName: 'France', flag: '🇫🇷', rank: 3, previousRank: 5, change: 2, category: 'Education #1' },
      { countryCode: 'JP', countryName: 'Japan', flag: '🇯🇵', rank: 6, previousRank: 8, change: 2, category: 'Education #2' },
      { countryCode: 'BR', countryName: 'Brazil', flag: '🇧🇷', rank: 1, previousRank: 1, change: 0, category: 'Education #1' }
    ],
    sentiment: {
      positive: 78,
      neutral: 14,
      negative: 8,
      topics: [
        { name: 'Features', sentiment: 'positive', mentionCount: 14200, sampleSnippet: 'Bite-sized streaks make practicing daily seamless.' },
        { name: 'Design', sentiment: 'positive', mentionCount: 8900, sampleSnippet: 'Duo widget and animations are ultra engaging.' },
        { name: 'Subscriptions', sentiment: 'neutral', mentionCount: 7400, sampleSnippet: 'Super Duolingo is pricey but helpful for hearts.' },
        { name: 'Performance', sentiment: 'positive', mentionCount: 6100, sampleSnippet: 'Loads instantly without battery drain.' },
        { name: 'Bugs', sentiment: 'positive', mentionCount: 1200, sampleSnippet: 'Voice recognition occasionally misinterprets audio.' }
      ]
    },
    averageUpdateDays: 11,
    versions: [
      { version: '8.12.0', releaseDate: 'August 18, 2026', releaseNotes: 'Revamped Math and Music course modules with new interactive exercises.', daysSinceLastUpdate: 7 },
      { version: '8.11.2', releaseDate: 'August 06, 2026', releaseNotes: 'Performance optimizations and widget streak synchronization improvements.', daysSinceLastUpdate: 19 },
      { version: '8.10.0', releaseDate: 'July 24, 2026', releaseNotes: 'New social leaderboard leagues and friend quest achievements.', daysSinceLastUpdate: 32 }
    ],
    keywords: [
      { keyword: 'language learning', rank: 1, previousRank: 1, searchVolume: 96, difficulty: 'High', movement: 0 },
      { keyword: 'spanish lessons', rank: 1, previousRank: 3, searchVolume: 91, difficulty: 'High', movement: 2, isOpportunity: true },
      { keyword: 'learn french free', rank: 2, previousRank: 4, searchVolume: 84, difficulty: 'Medium', movement: 2 },
      { keyword: 'english vocabulary', rank: 3, previousRank: 6, searchVolume: 88, difficulty: 'High', movement: 3 },
      { keyword: 'speak japanese', rank: 4, previousRank: 5, searchVolume: 79, difficulty: 'Medium', movement: 1 },
      { keyword: 'duo widget', rank: 1, previousRank: 1, searchVolume: 82, difficulty: 'Low', movement: 0 }
    ],
    competitorIds: ['babbel', 'busuu', 'memrise', 'rosetta-stone'],
    aiInsights: [
      { id: 'duo-1', type: 'velocity', title: 'Breakout Momentum', text: 'Duolingo is growing 3.8× faster than the Education category average this week.', impact: 'high', timestamp: '2h ago', appId: 'duolingo', appName: 'Duolingo' },
      { id: 'duo-2', type: 'ranking', title: 'US Ranking Surge', text: 'The app gained 2 ranking positions in the United States over the last 48 hours.', impact: 'positive', timestamp: '6h ago', appId: 'duolingo', appName: 'Duolingo' },
      { id: 'duo-3', type: 'review', title: 'Review Velocity Increase', text: '5-star review frequency jumped +18% following version 8.12.0 release.', impact: 'positive', timestamp: '1d ago', appId: 'duolingo', appName: 'Duolingo' }
    ],
    tags: ['Education', 'Language', 'Self-Improvement', 'Editor Choice'],
    trendingCategories: ['breakout', 'fastest_growing']
  },

  'chatgpt': {
    id: 'chatgpt',
    slug: 'chatgpt',
    name: 'ChatGPT',
    developer: 'OpenAI',
    developerName: 'OpenAI',
    developerSlug: 'openai',
    appStoreId: 6448311069,
    googlePlayId: 'com.openai.chatgpt',
    bundleId: 'com.openai.chat',
    iconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/62/11/63/6211631a-f4ca-980d-ccdc-ac8f052e4662/AppIcon-0-0-1x_U007epad-0-0-0-1-0-P3-85-220.png/512x512bb.jpg',
    appStoreIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/62/11/63/6211631a-f4ca-980d-ccdc-ac8f052e4662/AppIcon-0-0-1x_U007epad-0-0-0-1-0-P3-85-220.png/512x512bb.jpg',
    googlePlayIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/62/11/63/6211631a-f4ca-980d-ccdc-ac8f052e4662/AppIcon-0-0-1x_U007epad-0-0-0-1-0-P3-85-220.png/512x512bb.jpg',
    category: 'Productivity',
    categorySlug: 'productivity',
    stores: ['appstore', 'googleplay'],
    primaryStore: 'appstore',
    subtitle: 'Get answers, find inspiration, be more productive',
    description: 'ChatGPT syncs your history across devices, brings you the latest model improvements from OpenAI, and provides instant answers.',
    price: 'Free',
    inAppPurchases: true,
    dataSource: 'Official',
    appStoreUrl: 'https://apps.apple.com/us/app/chatgpt/id6448311069',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.openai.chatgpt',
    firstReleaseDate: '2023-05-18',
    lastUpdated: '2026-08-21',
    currentRank: 1,
    previousRank: 1,
    categoryRank: 1,
    growthScore: 99,
    growthClassification: 'Breakout',
    growthVelocityPct: 24.8,
    rating: 4.9,
    totalReviews: 4120000,
    ratingCount: 4120000,
    ratingDistribution: { star5: 3500000, star4: 420000, star3: 110000, star2: 45000, star1: 45000 },
    estimatedMonthlyDownloads: 24500000,
    estimatedMonthlyRevenueUsd: 82000000,
    estimatedGrowthMoM: 32.4,
    isEstimated: true,
    outlier: {
      isOutlier: true,
      level: 'extreme',
      multiplier: 5.8,
      growthVs30dAveragePct: 314,
      normalVelocity: '+4.5% / week',
      currentVelocity: '+24.8% / week',
      detectedAt: '2026-08-24',
      summary: 'Extreme Outlier: ChatGPT increased 314% faster than its 30-day baseline across 140 countries.'
    },
    growthFactors: { rankingVelocity: 99, reviewGrowth: 99, ratingMomentum: 98, categoryMovement: 99, countryExpansion: 99, updateActivity: 96 },
    rankingsHistory: {
      '24H': generateRankings(1, 1, 24, 0),
      '7D': generateRankings(2, 1, 7, 0.3),
      '30D': generateRankings(3, 1, 30, 0.8),
      '90D': generateRankings(6, 1, 90, 1.2),
      '1Y': generateRankings(15, 1, 52, 2.5),
      'ALL': generateRankings(40, 1, 100, 3)
    },
    countryRankings: [
      { countryCode: 'US', countryName: 'United States', flag: '🇺🇸', rank: 1, previousRank: 1, change: 0, category: 'Productivity #1' },
      { countryCode: 'GB', countryName: 'United Kingdom', flag: '🇬🇧', rank: 1, previousRank: 2, change: 1, category: 'Productivity #1' },
      { countryCode: 'DE', countryName: 'Germany', flag: '🇩🇪', rank: 1, previousRank: 1, change: 0, category: 'Productivity #1' },
      { countryCode: 'JP', countryName: 'Japan', flag: '🇯🇵', rank: 2, previousRank: 3, change: 1, category: 'Productivity #1' },
      { countryCode: 'FR', countryName: 'France', flag: '🇫🇷', rank: 1, previousRank: 2, change: 1, category: 'Productivity #1' },
      { countryCode: 'BR', countryName: 'Brazil', flag: '🇧🇷', rank: 1, previousRank: 1, change: 0, category: 'Productivity #1' }
    ],
    sentiment: {
      positive: 84, neutral: 10, negative: 6,
      topics: [
        { name: 'Features', sentiment: 'positive', mentionCount: 45000, sampleSnippet: 'Voice mode is instantaneous and uncannily conversational.' },
        { name: 'Performance', sentiment: 'positive', mentionCount: 28000, sampleSnippet: 'Responses stream in under 400ms consistently.' },
        { name: 'Subscriptions', sentiment: 'neutral', mentionCount: 18000, sampleSnippet: 'Plus tier is high value for daily work.' }
      ]
    },
    averageUpdateDays: 8,
    versions: [
      { version: '2.40.0', releaseDate: 'August 21, 2026', releaseNotes: 'Real-time multi-modal live audio updates and workspace context switching.' },
      { version: '2.39.1', releaseDate: 'August 12, 2026', releaseNotes: 'Faster background generation and memory index sync.' }
    ],
    keywords: [
      { keyword: 'ai chat', rank: 1, previousRank: 1, searchVolume: 99, difficulty: 'High', movement: 0 },
      { keyword: 'chatbot assistant', rank: 1, previousRank: 2, searchVolume: 94, difficulty: 'High', movement: 1 },
      { keyword: 'ai writer', rank: 1, previousRank: 1, searchVolume: 90, difficulty: 'High', movement: 0 },
      { keyword: 'voice ai assistant', rank: 1, previousRank: 3, searchVolume: 86, difficulty: 'Medium', movement: 2, isOpportunity: true }
    ],
    competitorIds: ['notion', 'canva'],
    aiInsights: [
      { id: 'cg-1', type: 'velocity', title: 'Top Grossing Surge', text: 'ChatGPT reached #1 Top Grossing Productivity in 92 store regions.', impact: 'high', timestamp: '1h ago', appId: 'chatgpt', appName: 'ChatGPT' }
    ],
    tags: ['AI', 'Productivity', 'Assistant', 'Trending #1'],
    trendingCategories: ['breakout', 'fastest_growing', 'ai']
  },

  'spotify': {
    id: 'spotify',
    slug: 'spotify',
    name: 'Spotify: Music and Podcasts',
    developer: 'Spotify AB',
    developerName: 'Spotify AB',
    developerSlug: 'spotify',
    appStoreId: 324684580,
    googlePlayId: 'com.spotify.music',
    bundleId: 'com.spotify.client',
    iconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/78/d3/2a/78d32a70-87dc-58e9-86c7-04b87c88f873/AppIcon-0-0-1x_U007epad-0-1-0-0-sRGB-85-220.png/512x512bb.jpg',
    appStoreIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/78/d3/2a/78d32a70-87dc-58e9-86c7-04b87c88f873/AppIcon-0-0-1x_U007epad-0-1-0-0-sRGB-85-220.png/512x512bb.jpg',
    googlePlayIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/78/d3/2a/78d32a70-87dc-58e9-86c7-04b87c88f873/AppIcon-0-0-1x_U007epad-0-1-0-0-sRGB-85-220.png/512x512bb.jpg',
    category: 'Music',
    categorySlug: 'music',
    stores: ['appstore', 'googleplay'],
    primaryStore: 'appstore',
    subtitle: 'Stream songs, albums and audiobooks',
    description: 'Listen to the music and podcasts you love and find recommendations tailored just for you.',
    price: 'Free',
    inAppPurchases: true,
    dataSource: 'Official',
    appStoreUrl: 'https://apps.apple.com/us/app/spotify-music-and-podcasts/id324684580',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.spotify.music',
    firstReleaseDate: '2009-08-07',
    lastUpdated: '2026-08-19',
    currentRank: 3,
    previousRank: 3,
    categoryRank: 1,
    growthScore: 88,
    growthClassification: 'Fast Growing',
    growthVelocityPct: 8.5,
    rating: 4.8,
    totalReviews: 8900000,
    ratingCount: 8900000,
    ratingDistribution: { star5: 7120000, star4: 1100000, star3: 400000, star2: 150000, star1: 130000 },
    estimatedMonthlyDownloads: 18400000,
    estimatedMonthlyRevenueUsd: 54000000,
    estimatedGrowthMoM: 7.2,
    isEstimated: true,
    outlier: { isOutlier: false, level: 'none', multiplier: 1.1, growthVs30dAveragePct: 12, normalVelocity: '+6% / week', currentVelocity: '+8.5% / week', detectedAt: '2026-08-20', summary: 'Stable high velocity leader in Music.' },
    growthFactors: { rankingVelocity: 86, reviewGrowth: 90, ratingMomentum: 88, categoryMovement: 89, countryExpansion: 91, updateActivity: 85 },
    rankingsHistory: {
      '24H': generateRankings(3, 3, 24, 0.2),
      '7D': generateRankings(3, 3, 7, 0.3),
      '30D': generateRankings(4, 3, 30, 0.5),
      '90D': generateRankings(5, 3, 90, 1),
      '1Y': generateRankings(6, 3, 52, 1.5),
      'ALL': generateRankings(10, 3, 100, 2)
    },
    countryRankings: [
      { countryCode: 'US', countryName: 'United States', flag: '🇺🇸', rank: 3, previousRank: 3, change: 0, category: 'Music #1' },
      { countryCode: 'GB', countryName: 'United Kingdom', flag: '🇬🇧', rank: 2, previousRank: 2, change: 0, category: 'Music #1' },
      { countryCode: 'DE', countryName: 'Germany', flag: '🇩🇪', rank: 2, previousRank: 3, change: 1, category: 'Music #1' },
      { countryCode: 'SE', countryName: 'Sweden', flag: '🇸🇪', rank: 1, previousRank: 1, change: 0, category: 'Music #1' }
    ],
    sentiment: { positive: 75, neutral: 16, negative: 9, topics: [{ name: 'Features', sentiment: 'positive', mentionCount: 32000, sampleSnippet: 'AI DJ and Daylist playlists are unmatched.' }] },
    averageUpdateDays: 7,
    versions: [{ version: '8.9.44', releaseDate: 'August 19, 2026', releaseNotes: 'Lossless audio playback enhancements and CarPlay UI updates.' }],
    keywords: [
      { keyword: 'music streaming', rank: 1, previousRank: 1, searchVolume: 97, difficulty: 'High', movement: 0 },
      { keyword: 'podcasts app', rank: 1, previousRank: 2, searchVolume: 90, difficulty: 'High', movement: 1 }
    ],
    competitorIds: [],
    aiInsights: [
      { id: 'sp-1', type: 'ranking', title: 'Market Consistency', text: 'Spotify holds top 3 overall ranking across 68 international markets.', impact: 'positive', timestamp: '1d ago', appId: 'spotify', appName: 'Spotify' }
    ],
    tags: ['Music', 'Podcasts', 'Audio', 'Streaming'],
    trendingCategories: ['fastest_growing']
  },

  'instagram': {
    id: 'instagram',
    slug: 'instagram',
    name: 'Instagram',
    developer: 'Meta Platforms, Inc.',
    developerName: 'Meta Platforms, Inc.',
    developerSlug: 'meta',
    appStoreId: 389801252,
    googlePlayId: 'com.instagram.android',
    bundleId: 'com.burbn.instagram',
    iconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/23/59/e9/2359e92d-376c-cc29-b9e6-ab9a4a00fcf4/Prod-0-0-1x_U007epad-0-1-0-sRGB-85-220.png/512x512bb.jpg',
    appStoreIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/23/59/e9/2359e92d-376c-cc29-b9e6-ab9a4a00fcf4/Prod-0-0-1x_U007epad-0-1-0-sRGB-85-220.png/512x512bb.jpg',
    googlePlayIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/23/59/e9/2359e92d-376c-cc29-b9e6-ab9a4a00fcf4/Prod-0-0-1x_U007epad-0-1-0-sRGB-85-220.png/512x512bb.jpg',
    category: 'Photo & Video',
    categorySlug: 'photo-video',
    stores: ['appstore', 'googleplay'],
    primaryStore: 'appstore',
    subtitle: 'Create, share, and connect',
    description: 'Instagram brings you closer to the people and things you love.',
    price: 'Free',
    inAppPurchases: true,
    dataSource: 'Official',
    appStoreUrl: 'https://apps.apple.com/us/app/instagram/id389801252',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.instagram.android',
    firstReleaseDate: '2010-10-06',
    lastUpdated: '2026-08-22',
    currentRank: 5,
    previousRank: 6,
    categoryRank: 1,
    growthScore: 85,
    growthClassification: 'Fast Growing',
    growthVelocityPct: 6.2,
    rating: 4.7,
    totalReviews: 12500000,
    ratingCount: 12500000,
    ratingDistribution: { star5: 9800000, star4: 1600000, star3: 600000, star2: 250000, star1: 250000 },
    estimatedMonthlyDownloads: 28000000,
    estimatedMonthlyRevenueUsd: 110000000,
    estimatedGrowthMoM: 4.1,
    isEstimated: true,
    outlier: { isOutlier: false, level: 'none', multiplier: 1.0, growthVs30dAveragePct: 5, normalVelocity: '+5% / week', currentVelocity: '+6.2% / week', detectedAt: '2026-08-18', summary: 'Global staple app.' },
    growthFactors: { rankingVelocity: 82, reviewGrowth: 88, ratingMomentum: 84, categoryMovement: 86, countryExpansion: 90, updateActivity: 92 },
    rankingsHistory: {
      '24H': generateRankings(5, 5, 24, 0.2),
      '7D': generateRankings(6, 5, 7, 0.4),
      '30D': generateRankings(7, 5, 30, 0.8),
      '90D': generateRankings(8, 5, 90, 1.2),
      '1Y': generateRankings(10, 5, 52, 1.5),
      'ALL': generateRankings(12, 5, 100, 2)
    },
    countryRankings: [
      { countryCode: 'US', countryName: 'United States', flag: '🇺🇸', rank: 5, previousRank: 6, change: 1, category: 'Photo & Video #1' },
      { countryCode: 'BR', countryName: 'Brazil', flag: '🇧🇷', rank: 2, previousRank: 2, change: 0, category: 'Photo & Video #1' }
    ],
    sentiment: { positive: 70, neutral: 18, negative: 12, topics: [{ name: 'Features', sentiment: 'positive', mentionCount: 50000, sampleSnippet: 'Reels and creator tools keep improving.' }] },
    averageUpdateDays: 5,
    versions: [{ version: '342.0', releaseDate: 'August 22, 2026', releaseNotes: 'Bug fixes and performance improvements for Reels camera.' }],
    keywords: [{ keyword: 'photo sharing', rank: 1, previousRank: 1, searchVolume: 98, difficulty: 'High', movement: 0 }],
    competitorIds: ['tiktok', 'capcut'],
    aiInsights: [{ id: 'ig-1', type: 'velocity', title: 'Steady Engagement', text: 'Instagram continues to capture 35% of daily social photo/video sessions.', impact: 'positive', timestamp: '2d ago', appId: 'instagram', appName: 'Instagram' }],
    tags: ['Social', 'Photo & Video', 'Stories', 'Reels'],
    trendingCategories: ['social']
  },

  'tiktok': {
    id: 'tiktok',
    slug: 'tiktok',
    name: 'TikTok',
    developer: 'ByteDance Ltd.',
    developerName: 'ByteDance Ltd.',
    developerSlug: 'bytedance',
    appStoreId: 835599320,
    googlePlayId: 'com.zhiliaoapp.musically',
    bundleId: 'com.zhiliaoapp.musically',
    iconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/b4/f6/9e/b4f69e23-a20c-784c-7f41-400fd8ab3d1c/TikTok_AppIcon26-0-0-1x_U007epad-0-1-0-85-220.png/512x512bb.jpg',
    appStoreIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/b4/f6/9e/b4f69e23-a20c-784c-7f41-400fd8ab3d1c/TikTok_AppIcon26-0-0-1x_U007epad-0-1-0-85-220.png/512x512bb.jpg',
    googlePlayIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/b4/f6/9e/b4f69e23-a20c-784c-7f41-400fd8ab3d1c/TikTok_AppIcon26-0-0-1x_U007epad-0-1-0-85-220.png/512x512bb.jpg',
    category: 'Entertainment',
    categorySlug: 'entertainment',
    stores: ['appstore', 'googleplay'],
    primaryStore: 'appstore',
    subtitle: 'Videos, Music & LIVE Streams',
    description: 'TikTok is THE destination for mobile videos. On TikTok, short-form videos are exciting, spontaneous, and genuine.',
    price: 'Free',
    inAppPurchases: true,
    dataSource: 'Official',
    appStoreUrl: 'https://apps.apple.com/us/app/tiktok/id478354252',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.zhiliaoapp.musically',
    firstReleaseDate: '2015-05-07',
    lastUpdated: '2026-08-20',
    currentRank: 4,
    previousRank: 5,
    categoryRank: 1,
    growthScore: 91,
    growthClassification: 'Breakout',
    growthVelocityPct: 15.3,
    rating: 4.6,
    totalReviews: 16800000,
    ratingCount: 16800000,
    ratingDistribution: { star5: 12500000, star4: 2100000, star3: 1100000, star2: 600000, star1: 500000 },
    estimatedMonthlyDownloads: 32000000,
    estimatedMonthlyRevenueUsd: 145000000,
    estimatedGrowthMoM: 12.8,
    isEstimated: true,
    outlier: { isOutlier: true, level: 'minor', multiplier: 2.1, growthVs30dAveragePct: 115, normalVelocity: '+7.2% / week', currentVelocity: '+15.3% / week', detectedAt: '2026-08-23', summary: 'Accelerating download momentum in Southeast Asia & Europe.' },
    growthFactors: { rankingVelocity: 92, reviewGrowth: 94, ratingMomentum: 88, categoryMovement: 93, countryExpansion: 95, updateActivity: 89 },
    rankingsHistory: {
      '24H': generateRankings(4, 4, 24, 0.2),
      '7D': generateRankings(5, 4, 7, 0.4),
      '30D': generateRankings(6, 4, 30, 0.9),
      '90D': generateRankings(8, 4, 90, 1.4),
      '1Y': generateRankings(9, 4, 52, 1.8),
      'ALL': generateRankings(15, 4, 100, 2.5)
    },
    countryRankings: [
      { countryCode: 'US', countryName: 'United States', flag: '🇺🇸', rank: 4, previousRank: 5, change: 1, category: 'Entertainment #1' },
      { countryCode: 'ID', countryName: 'Indonesia', flag: '🇮🇩', rank: 1, previousRank: 1, change: 0, category: 'Entertainment #1' }
    ],
    sentiment: { positive: 72, neutral: 16, negative: 12, topics: [{ name: 'Features', sentiment: 'positive', mentionCount: 65000, sampleSnippet: 'Algorithm serves high affinity video feeds effortlessly.' }] },
    averageUpdateDays: 6,
    versions: [{ version: '35.8.0', releaseDate: 'August 20, 2026', releaseNotes: 'Enhanced creation filters and direct message search improvements.' }],
    keywords: [{ keyword: 'short video', rank: 1, previousRank: 1, searchVolume: 99, difficulty: 'High', movement: 0 }],
    competitorIds: ['instagram', 'capcut'],
    aiInsights: [{ id: 'tt-1', type: 'velocity', title: 'Creator Velocity', text: 'TikTok in-app purchase revenue grew +14% month-over-month.', impact: 'positive', timestamp: '1d ago', appId: 'tiktok', appName: 'TikTok' }],
    tags: ['Entertainment', 'Video', 'Social', 'Viral'],
    trendingCategories: ['breakout', 'social']
  },

  'canva': {
    id: 'canva',
    slug: 'canva',
    name: 'Canva: Design, Photo & Video',
    developer: 'Canva Pty Ltd',
    developerName: 'Canva Pty Ltd',
    developerSlug: 'canva',
    appStoreId: 897446215,
    googlePlayId: 'com.canva.editor',
    bundleId: 'com.canva.Canva',
    iconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/ad/8a/a8/ad8aa8cf-734e-96bc-162a-2cec006ffc12/AppIcon-0-0-1x_U007epad-0-11-0-85-220.png/512x512bb.jpg',
    appStoreIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/ad/8a/a8/ad8aa8cf-734e-96bc-162a-2cec006ffc12/AppIcon-0-0-1x_U007epad-0-11-0-85-220.png/512x512bb.jpg',
    googlePlayIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/ad/8a/a8/ad8aa8cf-734e-96bc-162a-2cec006ffc12/AppIcon-0-0-1x_U007epad-0-11-0-85-220.png/512x512bb.jpg',
    category: 'Productivity',
    categorySlug: 'productivity',
    stores: ['appstore', 'googleplay'],
    primaryStore: 'appstore',
    subtitle: 'Graphic design, logo & presentations',
    description: 'Canva makes design amazingly simple and fun! Create stunning graphics, logos, presentations, social media posts and video edits.',
    price: 'Free',
    inAppPurchases: true,
    dataSource: 'Official',
    appStoreUrl: 'https://apps.apple.com/us/app/canva-design-photo-video/id897446215',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.canva.editor',
    firstReleaseDate: '2014-10-23',
    lastUpdated: '2026-08-14',
    currentRank: 7,
    previousRank: 9,
    categoryRank: 2,
    growthScore: 89,
    growthClassification: 'Fast Growing',
    growthVelocityPct: 12.1,
    rating: 4.9,
    totalReviews: 3800000,
    ratingCount: 3800000,
    ratingDistribution: { star5: 3200000, star4: 450000, star3: 90000, star2: 30000, star1: 30000 },
    estimatedMonthlyDownloads: 8900000,
    estimatedMonthlyRevenueUsd: 22000000,
    estimatedGrowthMoM: 14.5,
    isEstimated: true,
    outlier: { isOutlier: true, level: 'minor', multiplier: 2.3, growthVs30dAveragePct: 130, normalVelocity: '+5% / week', currentVelocity: '+12.1% / week', detectedAt: '2026-08-21', summary: 'AI design tools driven breakout adoption among small business founders.' },
    growthFactors: { rankingVelocity: 88, reviewGrowth: 92, ratingMomentum: 94, categoryMovement: 90, countryExpansion: 88, updateActivity: 84 },
    rankingsHistory: {
      '24H': generateRankings(8, 7, 24, 0.3),
      '7D': generateRankings(9, 7, 7, 0.5),
      '30D': generateRankings(14, 7, 30, 1.1),
      '90D': generateRankings(20, 7, 90, 1.8),
      '1Y': generateRankings(32, 7, 52, 2.5),
      'ALL': generateRankings(50, 7, 100, 3.5)
    },
    countryRankings: [
      { countryCode: 'US', countryName: 'United States', flag: '🇺🇸', rank: 7, previousRank: 9, change: 2, category: 'Productivity #2' },
      { countryCode: 'AU', countryName: 'Australia', flag: '🇦🇺', rank: 2, previousRank: 3, change: 1, category: 'Productivity #1' }
    ],
    sentiment: { positive: 88, neutral: 8, negative: 4, topics: [{ name: 'Design', sentiment: 'positive', mentionCount: 28000, sampleSnippet: 'Magic Studio AI turns prompts into pitch decks in seconds.' }] },
    averageUpdateDays: 14,
    versions: [{ version: '4.22.0', releaseDate: 'August 14, 2026', releaseNotes: 'Magic Switch translation and one-click brand kit application.' }],
    keywords: [
      { keyword: 'graphic design', rank: 1, previousRank: 1, searchVolume: 94, difficulty: 'High', movement: 0 },
      { keyword: 'logo maker', rank: 1, previousRank: 2, searchVolume: 89, difficulty: 'High', movement: 1 }
    ],
    competitorIds: ['notion', 'capcut'],
    aiInsights: [{ id: 'cv-1', type: 'velocity', title: 'Cross-Category Expansion', text: 'Canva captured #2 Productivity spot ahead of legacy office apps.', impact: 'positive', timestamp: '2d ago', appId: 'canva', appName: 'Canva' }],
    tags: ['Design', 'Productivity', 'Graphics', 'AI'],
    trendingCategories: ['productivity', 'ai']
  },

  'notion': {
    id: 'notion',
    slug: 'notion',
    name: 'Notion: Notes, Docs, Tasks',
    developer: 'Notion Labs, Inc.',
    developerName: 'Notion Labs, Inc.',
    developerSlug: 'notion',
    appStoreId: 1232780281,
    googlePlayId: 'notion.id',
    bundleId: 'notion.id',
    iconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/3f/84/6a/3f846aa0-0597-016c-bd4f-3a7c44843a99/AppIconProd-0-0-1x_U007epad-0-0-0-1-0-0-P3-85-220.png/512x512bb.jpg',
    appStoreIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/3f/84/6a/3f846aa0-0597-016c-bd4f-3a7c44843a99/AppIconProd-0-0-1x_U007epad-0-0-0-1-0-0-P3-85-220.png/512x512bb.jpg',
    googlePlayIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/3f/84/6a/3f846aa0-0597-016c-bd4f-3a7c44843a99/AppIconProd-0-0-1x_U007epad-0-0-0-1-0-0-P3-85-220.png/512x512bb.jpg',
    category: 'Productivity',
    categorySlug: 'productivity',
    stores: ['appstore', 'googleplay'],
    primaryStore: 'appstore',
    subtitle: 'Write, plan, organize, with AI',
    description: 'A new tool that blends your everyday work apps into one. It’s the all-in-one workspace for you and your team.',
    price: 'Free',
    inAppPurchases: true,
    dataSource: 'Official',
    appStoreUrl: 'https://apps.apple.com/us/app/notion-notes-docs-tasks/id1232780281',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=notion.id',
    firstReleaseDate: '2017-06-08',
    lastUpdated: '2026-08-16',
    currentRank: 12,
    previousRank: 16,
    categoryRank: 3,
    growthScore: 92,
    growthClassification: 'Breakout',
    growthVelocityPct: 19.4,
    rating: 4.8,
    totalReviews: 890000,
    ratingCount: 890000,
    ratingDistribution: { star5: 740000, star4: 100000, star3: 30000, star2: 10000, star1: 10000 },
    estimatedMonthlyDownloads: 4100000,
    estimatedMonthlyRevenueUsd: 14200000,
    estimatedGrowthMoM: 22.1,
    isEstimated: true,
    outlier: { isOutlier: true, level: 'strong', multiplier: 3.4, growthVs30dAveragePct: 185, normalVelocity: '+5.5% / week', currentVelocity: '+19.4% / week', detectedAt: '2026-08-23', summary: 'Strong Outlier: Notion Q3 Calendar and Database sync triggered 185% velocity surge.' },
    growthFactors: { rankingVelocity: 94, reviewGrowth: 95, ratingMomentum: 92, categoryMovement: 93, countryExpansion: 90, updateActivity: 88 },
    rankingsHistory: {
      '24H': generateRankings(13, 12, 24, 0.3),
      '7D': generateRankings(16, 12, 7, 0.7),
      '30D': generateRankings(26, 12, 30, 1.4),
      '90D': generateRankings(42, 12, 90, 2.2),
      '1Y': generateRankings(68, 12, 52, 3.5),
      'ALL': generateRankings(95, 12, 100, 4.5)
    },
    countryRankings: [
      { countryCode: 'US', countryName: 'United States', flag: '🇺🇸', rank: 12, previousRank: 16, change: 4, category: 'Productivity #3' },
      { countryCode: 'DE', countryName: 'Germany', flag: '🇩🇪', rank: 8, previousRank: 14, change: 6, category: 'Productivity #2' },
      { countryCode: 'JP', countryName: 'Japan', flag: '🇯🇵', rank: 5, previousRank: 9, change: 4, category: 'Productivity #1' }
    ],
    sentiment: { positive: 82, neutral: 12, negative: 6, topics: [{ name: 'Features', sentiment: 'positive', mentionCount: 16000, sampleSnippet: 'Databases and AI Q&A make knowledge management instant.' }] },
    averageUpdateDays: 10,
    versions: [{ version: '2.48.0', releaseDate: 'August 16, 2026', releaseNotes: 'Offline sync improvements, custom home widgets, and Notion AI auto-fill.' }],
    keywords: [
      { keyword: 'notes app', rank: 2, previousRank: 3, searchVolume: 92, difficulty: 'High', movement: 1 },
      { keyword: 'project management', rank: 3, previousRank: 5, searchVolume: 88, difficulty: 'Medium', movement: 2, isOpportunity: true }
    ],
    competitorIds: ['chatgpt', 'canva'],
    aiInsights: [{ id: 'nt-1', type: 'velocity', title: 'Enterprise Adoption Acceleration', text: 'Notion jumped +6 positions in Germany following localization launch.', impact: 'high', timestamp: '1d ago', appId: 'notion', appName: 'Notion' }],
    tags: ['Productivity', 'Notes', 'Team', 'AI'],
    trendingCategories: ['breakout', 'productivity', 'ai']
  },

  'capcut': {
    id: 'capcut',
    slug: 'capcut',
    name: 'CapCut - Video Editor',
    developer: 'ByteDance Ltd.',
    developerName: 'ByteDance Ltd.',
    developerSlug: 'bytedance',
    appStoreId: 1500855883,
    googlePlayId: 'com.lemon.lvoverseas',
    bundleId: 'com.lemon.lvoverseas',
    iconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/e5/66/e5/e566e522-06cc-b4a9-c541-cfa8c35b8540/AppIcon-0-0-1x_U007emarketing-0-8-0-85-220.png/512x512bb.jpg',
    appStoreIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/e5/66/e5/e566e522-06cc-b4a9-c541-cfa8c35b8540/AppIcon-0-0-1x_U007emarketing-0-8-0-85-220.png/512x512bb.jpg',
    googlePlayIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/e5/66/e5/e566e522-06cc-b4a9-c541-cfa8c35b8540/AppIcon-0-0-1x_U007emarketing-0-8-0-85-220.png/512x512bb.jpg',
    category: 'Photo & Video',
    categorySlug: 'photo-video',
    stores: ['appstore', 'googleplay'],
    primaryStore: 'appstore',
    subtitle: 'Video editor, maker & templates',
    description: 'CapCut is an easy-to-use video editing app. It offers free, easy-to-use video editing features, free in-app fonts & effects, and advanced features.',
    price: 'Free',
    inAppPurchases: true,
    dataSource: 'Official',
    appStoreUrl: 'https://apps.apple.com/us/app/capcut-video-editor/id1500855883',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.lemon.lvoverseas',
    firstReleaseDate: '2020-04-10',
    lastUpdated: '2026-08-17',
    currentRank: 6,
    previousRank: 8,
    categoryRank: 2,
    growthScore: 94,
    growthClassification: 'Breakout',
    growthVelocityPct: 17.8,
    rating: 4.8,
    totalReviews: 6100000,
    ratingCount: 6100000,
    ratingDistribution: { star5: 5100000, star4: 700000, star3: 180000, star2: 60000, star1: 60000 },
    estimatedMonthlyDownloads: 21000000,
    estimatedMonthlyRevenueUsd: 48000000,
    estimatedGrowthMoM: 19.5,
    isEstimated: true,
    outlier: { isOutlier: true, level: 'strong', multiplier: 3.1, growthVs30dAveragePct: 165, normalVelocity: '+6.1% / week', currentVelocity: '+17.8% / week', detectedAt: '2026-08-23', summary: 'AI auto-captioning templates drove 165% velocity surge.' },
    growthFactors: { rankingVelocity: 95, reviewGrowth: 96, ratingMomentum: 92, categoryMovement: 94, countryExpansion: 93, updateActivity: 91 },
    rankingsHistory: {
      '24H': generateRankings(7, 6, 24, 0.2),
      '7D': generateRankings(8, 6, 7, 0.4),
      '30D': generateRankings(12, 6, 30, 1.0),
      '90D': generateRankings(18, 6, 90, 1.6),
      '1Y': generateRankings(28, 6, 52, 2.2),
      'ALL': generateRankings(40, 6, 100, 3.0)
    },
    countryRankings: [
      { countryCode: 'US', countryName: 'United States', flag: '🇺🇸', rank: 6, previousRank: 8, change: 2, category: 'Photo & Video #2' },
      { countryCode: 'GB', countryName: 'United Kingdom', flag: '🇬🇧', rank: 4, previousRank: 6, change: 2, category: 'Photo & Video #1' }
    ],
    sentiment: { positive: 81, neutral: 13, negative: 6, topics: [{ name: 'Features', sentiment: 'positive', mentionCount: 38000, sampleSnippet: 'Auto captions and beat sync make editing 10x faster.' }] },
    averageUpdateDays: 8,
    versions: [{ version: '12.4.0', releaseDate: 'August 17, 2026', releaseNotes: 'AI portrait relighting and 4K 60fps export stability.' }],
    keywords: [
      { keyword: 'video editor', rank: 1, previousRank: 1, searchVolume: 98, difficulty: 'High', movement: 0 },
      { keyword: 'tiktok editor', rank: 1, previousRank: 1, searchVolume: 93, difficulty: 'High', movement: 0 }
    ],
    competitorIds: ['instagram', 'canva'],
    aiInsights: [{ id: 'cc-1', type: 'velocity', title: 'Creator Ecosystem Synergy', text: 'CapCut template shares to social channels climbed +28% this month.', impact: 'high', timestamp: '12h ago', appId: 'capcut', appName: 'CapCut' }],
    tags: ['Video', 'Editing', 'AI', 'Creator'],
    trendingCategories: ['breakout', 'fastest_growing']
  },

  // Competitor Apps for Duolingo
  'babbel': {
    id: 'babbel',
    slug: 'babbel',
    name: 'Babbel - Language Learning',
    developer: 'Babbel GmbH',
    developerName: 'Babbel GmbH',
    developerSlug: 'babbel',
    appStoreId: 829587759,
    googlePlayId: 'com.babbel.mobile.android.en',
    bundleId: 'com.babbel.mobile',
    iconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/c9/38/eb/c938eb21-0770-fe08-1059-e9eb3409b2b9/AppIcon-Default-0-0-1x_U007epad-0-1-0-85-220.png/512x512bb.jpg',
    appStoreIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/c9/38/eb/c938eb21-0770-fe08-1059-e9eb3409b2b9/AppIcon-Default-0-0-1x_U007epad-0-1-0-85-220.png/512x512bb.jpg',
    googlePlayIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/c9/38/eb/c938eb21-0770-fe08-1059-e9eb3409b2b9/AppIcon-Default-0-0-1x_U007epad-0-1-0-85-220.png/512x512bb.jpg',
    category: 'Education',
    categorySlug: 'education',
    stores: ['appstore', 'googleplay'],
    primaryStore: 'appstore',
    subtitle: 'Conversational language practice',
    description: 'Speak a new language with confidence. Babbel combines live online classes with smart app-based lessons.',
    price: 'Free',
    inAppPurchases: true,
    dataSource: 'Official',
    appStoreUrl: 'https://apps.apple.com/us/app/babbel-language-learning/id383963833',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.babbel.mobile.android.en',
    firstReleaseDate: '2010-08-15',
    lastUpdated: '2026-08-10',
    currentRank: 18,
    previousRank: 22,
    categoryRank: 4,
    growthScore: 78,
    growthClassification: 'Growing',
    growthVelocityPct: 7.4,
    rating: 4.6,
    totalReviews: 720000,
    ratingCount: 720000,
    ratingDistribution: { star5: 540000, star4: 110000, star3: 40000, star2: 15000, star1: 15000 },
    estimatedMonthlyDownloads: 1800000,
    estimatedMonthlyRevenueUsd: 12500000,
    estimatedGrowthMoM: 6.2,
    isEstimated: true,
    outlier: { isOutlier: false, level: 'none', multiplier: 1.0, growthVs30dAveragePct: 8, normalVelocity: '+5% / week', currentVelocity: '+7.4% / week', detectedAt: '2026-08-15', summary: 'Steady revenue driven education performer.' },
    growthFactors: { rankingVelocity: 74, reviewGrowth: 78, ratingMomentum: 80, categoryMovement: 76, countryExpansion: 82, updateActivity: 79 },
    rankingsHistory: {
      '24H': generateRankings(19, 18, 24, 0.4),
      '7D': generateRankings(22, 18, 7, 0.8),
      '30D': generateRankings(28, 18, 30, 1.5),
      '90D': generateRankings(35, 18, 90, 2.2),
      '1Y': generateRankings(45, 18, 52, 3),
      'ALL': generateRankings(60, 18, 100, 4)
    },
    countryRankings: [
      { countryCode: 'DE', countryName: 'Germany', flag: '🇩🇪', rank: 3, previousRank: 4, change: 1, category: 'Education #1' },
      { countryCode: 'US', countryName: 'United States', flag: '🇺🇸', rank: 18, previousRank: 22, change: 4, category: 'Education #4' }
    ],
    sentiment: { positive: 76, neutral: 15, negative: 9, topics: [{ name: 'Pricing', sentiment: 'neutral', mentionCount: 8200, sampleSnippet: 'Subscription is solid for professional adult learners.' }] },
    averageUpdateDays: 14,
    versions: [{ version: '22.4.0', releaseDate: 'August 10, 2026', releaseNotes: 'Speech recognition engine upgrade for conversational lessons.' }],
    keywords: [{ keyword: 'learn spanish', rank: 3, previousRank: 4, searchVolume: 90, difficulty: 'High', movement: 1 }],
    competitorIds: ['duolingo', 'busuu', 'memrise', 'rosetta-stone'],
    aiInsights: [{ id: 'ba-1', type: 'competitor', title: 'Duolingo Comparison', text: 'Babbel captures 42% higher revenue per active user compared to freemium competitors.', impact: 'positive', timestamp: '2d ago', appId: 'babbel', appName: 'Babbel' }],
    tags: ['Education', 'Language', 'Grammar'],
    trendingCategories: ['productivity']
  },

  'busuu': {
    id: 'busuu',
    slug: 'busuu',
    name: 'Busuu: Learn Languages',
    developer: 'Busuu Limited',
    developerName: 'Busuu Limited',
    developerSlug: 'busuu',
    appStoreId: 379968583,
    googlePlayId: 'com.busuu.android.enc',
    bundleId: 'com.busuu.english.enc',
    iconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/a0/04/58/a00458dd-f001-30a4-247c-fda5f3adf4e3/AppIcon-0-0-1x_U007emarketing-0-8-0-sRGB-0-0-85-220.png/512x512bb.jpg',
    appStoreIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/a0/04/58/a00458dd-f001-30a4-247c-fda5f3adf4e3/AppIcon-0-0-1x_U007emarketing-0-8-0-sRGB-0-0-85-220.png/512x512bb.jpg',
    googlePlayIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/a0/04/58/a00458dd-f001-30a4-247c-fda5f3adf4e3/AppIcon-0-0-1x_U007emarketing-0-8-0-sRGB-0-0-85-220.png/512x512bb.jpg',
    category: 'Education',
    categorySlug: 'education',
    stores: ['appstore', 'googleplay'],
    primaryStore: 'appstore',
    subtitle: 'Learn Spanish, French & 12 more',
    description: 'Learn languages with Busuu, an app used by over 120 million learners worldwide with native speaker feedback.',
    price: 'Free',
    inAppPurchases: true,
    dataSource: 'Official',
    appStoreUrl: 'https://apps.apple.com/us/app/busuu-language-learning/id379968583',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.busuu.android.enc',
    firstReleaseDate: '2010-06-25',
    lastUpdated: '2026-08-12',
    currentRank: 34,
    previousRank: 42,
    categoryRank: 7,
    growthScore: 82,
    growthClassification: 'Fast Growing',
    growthVelocityPct: 11.8,
    rating: 4.7,
    totalReviews: 540000,
    ratingCount: 540000,
    ratingDistribution: { star5: 410000, star4: 85000, star3: 25000, star2: 10000, star1: 10000 },
    estimatedMonthlyDownloads: 1200000,
    estimatedMonthlyRevenueUsd: 6800000,
    estimatedGrowthMoM: 10.4,
    isEstimated: true,
    outlier: { isOutlier: false, level: 'none', multiplier: 1.4, growthVs30dAveragePct: 45, normalVelocity: '+4% / week', currentVelocity: '+11.8% / week', detectedAt: '2026-08-19', summary: 'Community feedback engagement driving steady growth.' },
    growthFactors: { rankingVelocity: 80, reviewGrowth: 84, ratingMomentum: 82, categoryMovement: 81, countryExpansion: 78, updateActivity: 85 },
    rankingsHistory: {
      '24H': generateRankings(36, 34, 24, 0.4),
      '7D': generateRankings(42, 34, 7, 1.0),
      '30D': generateRankings(55, 34, 30, 2.0),
      '90D': generateRankings(72, 34, 90, 3.0),
      '1Y': generateRankings(90, 34, 52, 4.0),
      'ALL': generateRankings(120, 34, 100, 5.0)
    },
    countryRankings: [
      { countryCode: 'GB', countryName: 'United Kingdom', flag: '🇬🇧', rank: 8, previousRank: 12, change: 4, category: 'Education #2' },
      { countryCode: 'US', countryName: 'United States', flag: '🇺🇸', rank: 34, previousRank: 42, change: 8, category: 'Education #7' }
    ],
    sentiment: { positive: 79, neutral: 14, negative: 7, topics: [{ name: 'Features', sentiment: 'positive', mentionCount: 6500, sampleSnippet: 'Native speakers correcting exercises is a huge differentiator.' }] },
    averageUpdateDays: 12,
    versions: [{ version: '26.8.0', releaseDate: 'August 12, 2026', releaseNotes: 'New certification tests aligned with CEFR standards.' }],
    keywords: [{ keyword: 'learn french', rank: 4, previousRank: 7, searchVolume: 82, difficulty: 'Medium', movement: 3 }],
    competitorIds: ['duolingo', 'babbel', 'memrise'],
    aiInsights: [{ id: 'bu-1', type: 'ranking', title: 'UK Movement', text: 'Busuu surged 4 positions in the UK Education Top 10.', impact: 'positive', timestamp: '3d ago', appId: 'busuu', appName: 'Busuu' }],
    tags: ['Education', 'Language', 'Community'],
    trendingCategories: ['fastest_growing']
  },

  'memrise': {
    id: 'memrise',
    slug: 'memrise',
    name: 'Memrise: Easy Language Learning',
    developer: 'Memrise',
    developerName: 'Memrise',
    developerSlug: 'memrise',
    appStoreId: 436655005,
    googlePlayId: 'com.memrise.android.memrisecompanion',
    bundleId: 'com.memrise',
    iconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/55/48/d4/5548d47f-d6cc-4f35-eb13-5fa69872e717/AppIcon-0-0-1x_U007emarketing-0-8-0-P3-0-85-220.png/512x512bb.jpg',
    appStoreIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/55/48/d4/5548d47f-d6cc-4f35-eb13-5fa69872e717/AppIcon-0-0-1x_U007emarketing-0-8-0-P3-0-85-220.png/512x512bb.jpg',
    googlePlayIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/55/48/d4/5548d47f-d6cc-4f35-eb13-5fa69872e717/AppIcon-0-0-1x_U007emarketing-0-8-0-P3-0-85-220.png/512x512bb.jpg',
    category: 'Education',
    categorySlug: 'education',
    stores: ['appstore', 'googleplay'],
    primaryStore: 'appstore',
    subtitle: 'Real videos of local speakers',
    description: 'Learn real language conversations with authentic native video clips and AI practice buddy.',
    price: 'Free',
    inAppPurchases: true,
    dataSource: 'Official',
    appStoreUrl: 'https://apps.apple.com/us/app/memrise-easy-language-learning/id436655005',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.memrise.android.memrisecompanion',
    firstReleaseDate: '2011-05-12',
    lastUpdated: '2026-08-08',
    currentRank: 48,
    previousRank: 56,
    categoryRank: 11,
    growthScore: 74,
    growthClassification: 'Growing',
    growthVelocityPct: 8.1,
    rating: 4.7,
    totalReviews: 430000,
    ratingCount: 430000,
    ratingDistribution: { star5: 320000, star4: 70000, star3: 25000, star2: 8000, star1: 7000 },
    estimatedMonthlyDownloads: 890000,
    estimatedMonthlyRevenueUsd: 3900000,
    estimatedGrowthMoM: 5.4,
    isEstimated: true,
    outlier: { isOutlier: false, level: 'none', multiplier: 1.0, growthVs30dAveragePct: 10, normalVelocity: '+4% / week', currentVelocity: '+8.1% / week', detectedAt: '2026-08-16', summary: 'Steady video-based learning app.' },
    growthFactors: { rankingVelocity: 72, reviewGrowth: 75, ratingMomentum: 76, categoryMovement: 74, countryExpansion: 73, updateActivity: 76 },
    rankingsHistory: {
      '24H': generateRankings(50, 48, 24, 0.4),
      '7D': generateRankings(56, 48, 7, 1.0),
      '30D': generateRankings(68, 48, 30, 2.0),
      '90D': generateRankings(80, 48, 90, 3.0),
      '1Y': generateRankings(95, 48, 52, 4.0),
      'ALL': generateRankings(130, 48, 100, 5.0)
    },
    countryRankings: [
      { countryCode: 'GB', countryName: 'United Kingdom', flag: '🇬🇧', rank: 14, previousRank: 18, change: 4, category: 'Education #4' },
      { countryCode: 'US', countryName: 'United States', flag: '🇺🇸', rank: 48, previousRank: 56, change: 8, category: 'Education #11' }
    ],
    sentiment: { positive: 78, neutral: 14, negative: 8, topics: [{ name: 'Features', sentiment: 'positive', mentionCount: 4200, sampleSnippet: 'Native local speaker video flashcards are memorable.' }] },
    averageUpdateDays: 16,
    versions: [{ version: '2026.8.1', releaseDate: 'August 08, 2026', releaseNotes: 'AI conversational partner voice tuning and memory algorithm boost.' }],
    keywords: [{ keyword: 'language flashcards', rank: 2, previousRank: 3, searchVolume: 78, difficulty: 'Medium', movement: 1 }],
    competitorIds: ['duolingo', 'babbel', 'busuu'],
    aiInsights: [{ id: 'me-1', type: 'velocity', title: 'Video Flashcard Retargeting', text: 'Memrise active user retention rose +6% with new daily streak video prompts.', impact: 'positive', timestamp: '4d ago', appId: 'memrise', appName: 'Memrise' }],
    tags: ['Education', 'Vocabulary', 'Video'],
    trendingCategories: ['fastest_growing']
  },

  'rosetta-stone': {
    id: 'rosetta-stone',
    slug: 'rosetta-stone',
    name: 'Rosetta Stone: Learn Languages',
    developer: 'Rosetta Stone Ltd.',
    developerName: 'Rosetta Stone Ltd.',
    developerSlug: 'rosetta-stone',
    appStoreId: 435588892,
    googlePlayId: 'air.com.rosettastone.mobile.CoursePlayer',
    bundleId: 'air.com.rosettastone.mobile.CoursePlayer',
    iconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/cb/06/60/cb066089-c26c-cec2-001d-46d18faae1c5/RosettaStoneClassic-0-0-1x_U007epad-0-1-0-sRGB-85-220.png/512x512bb.jpg',
    appStoreIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/cb/06/60/cb066089-c26c-cec2-001d-46d18faae1c5/RosettaStoneClassic-0-0-1x_U007epad-0-1-0-sRGB-85-220.png/512x512bb.jpg',
    googlePlayIconUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/cb/06/60/cb066089-c26c-cec2-001d-46d18faae1c5/RosettaStoneClassic-0-0-1x_U007epad-0-1-0-sRGB-85-220.png/512x512bb.jpg',
    category: 'Education',
    categorySlug: 'education',
    stores: ['appstore', 'googleplay'],
    primaryStore: 'appstore',
    subtitle: 'Immersion language method',
    description: 'Learn language intuitively with the world-renowned Rosetta Stone method without translations.',
    price: 'Free',
    inAppPurchases: true,
    dataSource: 'Official',
    appStoreUrl: 'https://apps.apple.com/us/app/rosetta-stone-learn-languages/id435588892',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=air.com.rosettastone.mobile.CoursePlayer',
    firstReleaseDate: '2011-06-20',
    lastUpdated: '2026-08-10',
    currentRank: 62,
    previousRank: 68,
    categoryRank: 16,
    growthScore: 68,
    growthClassification: 'Growing',
    growthVelocityPct: 4.2,
    rating: 4.8,
    totalReviews: 360000,
    ratingCount: 360000,
    ratingDistribution: { star5: 290000, star4: 45000, star3: 15000, star2: 5000, star1: 5000 },
    estimatedMonthlyDownloads: 450000,
    estimatedMonthlyRevenueUsd: 5800000,
    estimatedGrowthMoM: 3.1,
    isEstimated: true,
    outlier: { isOutlier: false, level: 'none', multiplier: 0.9, growthVs30dAveragePct: 2, normalVelocity: '+3.5% / week', currentVelocity: '+4.2% / week', detectedAt: '2026-08-10', summary: 'Heritage education brand.' },
    growthFactors: { rankingVelocity: 65, reviewGrowth: 70, ratingMomentum: 78, categoryMovement: 66, countryExpansion: 68, updateActivity: 62 },
    rankingsHistory: {
      '24H': generateRankings(63, 62, 24, 0.3),
      '7D': generateRankings(68, 62, 7, 0.8),
      '30D': generateRankings(74, 62, 30, 1.5),
      '90D': generateRankings(82, 62, 90, 2.0),
      '1Y': generateRankings(90, 62, 52, 2.5),
      'ALL': generateRankings(110, 62, 100, 3.5)
    },
    countryRankings: [
      { countryCode: 'US', countryName: 'United States', flag: '🇺🇸', rank: 62, previousRank: 68, change: 6, category: 'Education #16' }
    ],
    sentiment: { positive: 80, neutral: 12, negative: 8, topics: [{ name: 'Pricing', sentiment: 'neutral', mentionCount: 3100, sampleSnippet: 'Lifetime subscription makes it worthwhile.' }] },
    averageUpdateDays: 28,
    versions: [{ version: '10.5.0', releaseDate: 'July 28, 2026', releaseNotes: 'TruAccent speech recognition engine performance fixes.' }],
    keywords: [{ keyword: 'immersion language', rank: 1, previousRank: 1, searchVolume: 74, difficulty: 'Medium', movement: 0 }],
    competitorIds: ['duolingo', 'babbel'],
    aiInsights: [{ id: 'rs-1', type: 'ranking', title: 'Long-tail Keywords', text: 'Rosetta Stone maintains #1 rank for 42 high-intent language search queries.', impact: 'positive', timestamp: '5d ago', appId: 'rosetta-stone', appName: 'Rosetta Stone' }],
    tags: ['Education', 'Immersion', 'Speech'],
    trendingCategories: ['productivity']
  }
};

export const DEVELOPERS_DATA: Record<string, DeveloperData> = {
  'duolingo': {
    id: 'duolingo',
    slug: 'duolingo',
    name: 'Duolingo, Inc.',
    verified: true,
    avatarUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/67/e4/51/67e4516e-c1d0-452b-3f3e-5def33df3138/AppIcon-0-0-1x_U007epad-0-1-85-220.png/512x512bb.jpg',
    hqCountry: 'United States 🇺🇸',
    foundingYear: 2011,
    appsCount: 4,
    combinedEstimatedDownloads: 11400000,
    combinedEstimatedRevenueUsd: 32000000,
    averageRating: 4.82,
    growthScore: 95,
    rankingInStore: 1,
    categories: ['Education', 'Games', 'Kids'],
    topAppId: 'duolingo',
    appIds: ['duolingo'],
    about: 'Duolingo is the leading mobile learning platform globally, pioneering gamified education for languages, math, and music.',
    aiSummary: 'Duolingo operates one of the highest daily-retention engines in the app ecosystem, driven by streak mechanics and AI personalization.'
  },
  'openai': {
    id: 'openai',
    slug: 'openai',
    name: 'OpenAI',
    verified: true,
    avatarUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/62/11/63/6211631a-f4ca-980d-ccdc-ac8f052e4662/AppIcon-0-0-1x_U007epad-0-0-0-1-0-P3-85-220.png/512x512bb.jpg',
    hqCountry: 'United States 🇺🇸',
    foundingYear: 2015,
    appsCount: 2,
    combinedEstimatedDownloads: 26000000,
    combinedEstimatedRevenueUsd: 88000000,
    averageRating: 4.9,
    growthScore: 99,
    rankingInStore: 1,
    categories: ['Productivity', 'Utilities'],
    topAppId: 'chatgpt',
    appIds: ['chatgpt'],
    about: 'OpenAI is an AI research and deployment company developing friendly and useful artificial general intelligence.',
    aiSummary: 'OpenAI holds the #1 velocity in global app downloads across both iOS and Android stores with unprecedented multi-modal engagement.'
  },
  'spotify': {
    id: 'spotify',
    slug: 'spotify',
    name: 'Spotify AB',
    verified: true,
    avatarUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/78/d3/2a/78d32a70-87dc-58e9-86c7-04b87c88f873/AppIcon-0-0-1x_U007epad-0-1-0-0-sRGB-85-220.png/512x512bb.jpg',
    hqCountry: 'Sweden 🇸🇪',
    foundingYear: 2006,
    appsCount: 6,
    combinedEstimatedDownloads: 22000000,
    combinedEstimatedRevenueUsd: 62000000,
    averageRating: 4.78,
    growthScore: 88,
    rankingInStore: 3,
    categories: ['Music', 'Audiobooks', 'Podcasts'],
    topAppId: 'spotify',
    appIds: ['spotify'],
    about: 'Spotify transformed music listening forever when it launched in 2008. Discover, manage and share over 100M tracks and 5M podcasts.',
    aiSummary: 'Spotify sustains market leadership through AI DJ discovery features, audiobooks expansion, and strong cross-platform lock-in.'
  },
  'bytedance': {
    id: 'bytedance',
    slug: 'bytedance',
    name: 'ByteDance Ltd.',
    verified: true,
    avatarUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/b4/f6/9e/b4f69e23-a20c-784c-7f41-400fd8ab3d1c/TikTok_AppIcon26-0-0-1x_U007epad-0-1-0-85-220.png/512x512bb.jpg',
    hqCountry: 'Singapore 🇸🇬',
    foundingYear: 2012,
    appsCount: 18,
    combinedEstimatedDownloads: 58000000,
    combinedEstimatedRevenueUsd: 195000000,
    averageRating: 4.74,
    growthScore: 96,
    rankingInStore: 1,
    categories: ['Entertainment', 'Photo & Video', 'Social', 'Tools'],
    topAppId: 'tiktok',
    appIds: ['tiktok', 'capcut'],
    about: 'ByteDance’s mission is to inspire creativity and enrich life through innovative content platforms and editing suites.',
    aiSummary: 'ByteDance dominates mobile short-video and creator editing through the TikTok + CapCut flywheel with industry-leading virality.'
  },
  'meta': {
    id: 'meta',
    slug: 'meta',
    name: 'Meta Platforms, Inc.',
    verified: true,
    avatarUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/23/59/e9/2359e92d-376c-cc29-b9e6-ab9a4a00fcf4/Prod-0-0-1x_U007epad-0-1-0-sRGB-85-220.png/512x512bb.jpg',
    hqCountry: 'United States 🇺🇸',
    foundingYear: 2004,
    appsCount: 12,
    combinedEstimatedDownloads: 74000000,
    combinedEstimatedRevenueUsd: 210000000,
    averageRating: 4.65,
    growthScore: 87,
    rankingInStore: 2,
    categories: ['Social', 'Photo & Video', 'Communication'],
    topAppId: 'instagram',
    appIds: ['instagram'],
    about: 'Meta builds technologies that help people connect, find communities, and grow businesses.',
    aiSummary: 'Meta manages the world’s largest connected mobile user base across Instagram, WhatsApp, and Threads with deep advertising monetization.'
  },
  'notion': {
    id: 'notion',
    slug: 'notion',
    name: 'Notion Labs, Inc.',
    verified: true,
    avatarUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/3f/84/6a/3f846aa0-0597-016c-bd4f-3a7c44843a99/AppIconProd-0-0-1x_U007epad-0-0-0-1-0-0-P3-85-220.png/512x512bb.jpg',
    hqCountry: 'United States 🇺🇸',
    foundingYear: 2016,
    appsCount: 3,
    combinedEstimatedDownloads: 5200000,
    combinedEstimatedRevenueUsd: 16500000,
    averageRating: 4.81,
    growthScore: 92,
    rankingInStore: 5,
    categories: ['Productivity', 'Business', 'Utilities'],
    topAppId: 'notion',
    appIds: ['notion'],
    about: 'Notion combines notes, docs, project management, and AI inside one flexible workspace used by millions worldwide.',
    aiSummary: 'Notion is successfully converting single-player note takers into enterprise teams through rapid AI integration and custom databases.'
  },
  'canva': {
    id: 'canva',
    slug: 'canva',
    name: 'Canva Pty Ltd',
    verified: true,
    avatarUrl: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/ad/8a/a8/ad8aa8cf-734e-96bc-162a-2cec006ffc12/AppIcon-0-0-1x_U007epad-0-11-0-85-220.png/512x512bb.jpg',
    hqCountry: 'Australia 🇦🇺',
    foundingYear: 2012,
    appsCount: 2,
    combinedEstimatedDownloads: 9800000,
    combinedEstimatedRevenueUsd: 24000000,
    averageRating: 4.89,
    growthScore: 89,
    rankingInStore: 4,
    categories: ['Productivity', 'Design', 'Photo & Video'],
    topAppId: 'canva',
    appIds: ['canva'],
    about: 'Canva empowers everyone in the world to design anything and publish anywhere with intuitive AI-driven tools.',
    aiSummary: 'Canva holds top-tier user satisfaction in mobile design with a 4.89 average rating across 3.8M+ reviews.'
  }
};

export const GLOBAL_AI_INSIGHTS: AIInsightItem[] = [
  {
    id: 'ai-g-1',
    type: 'velocity',
    title: 'Category Breakout: AI Assistants',
    text: 'AI productivity apps are capturing 44% more weekly installs than traditional document editors across US & European storefronts.',
    impact: 'high',
    timestamp: '15m ago',
    appId: 'chatgpt',
    appName: 'ChatGPT'
  },
  {
    id: 'ai-g-2',
    type: 'anomaly',
    title: 'Outlier Surge: Duolingo Momentum',
    text: 'Duolingo is growing 3.8× faster than the Education category baseline following its interactive Math and Music rollout.',
    impact: 'high',
    timestamp: '42m ago',
    appId: 'duolingo',
    appName: 'Duolingo'
  },
  {
    id: 'ai-g-3',
    type: 'ranking',
    title: 'Ranking Shift: Notion in Europe',
    text: 'Notion surged 6 spots into top 10 in Germany and France following Q3 offline synchronization updates.',
    impact: 'positive',
    timestamp: '2h ago',
    appId: 'notion',
    appName: 'Notion'
  },
  {
    id: 'ai-g-4',
    type: 'competitor',
    title: 'Creator Economy Flywheel',
    text: 'CapCut template shares to TikTok and Instagram generated an estimated 21M monthly mobile downloads globally.',
    impact: 'positive',
    timestamp: '4h ago',
    appId: 'capcut',
    appName: 'CapCut'
  },
  {
    id: 'ai-g-5',
    type: 'review',
    title: 'Review Sentiment Wave',
    text: 'Positive review sentiment for voice-enabled AI utilities is up +23% quarter-over-quarter.',
    impact: 'positive',
    timestamp: '6h ago'
  }
];

export const CATEGORIES_LIST = [
  { name: 'All Categories', slug: 'all', icon: 'all', appCount: '2.4M+' },
  { name: 'Productivity', slug: 'productivity', icon: 'productivity', appCount: '142K' },
  { name: 'Education', slug: 'education', icon: 'education', appCount: '198K' },
  { name: 'AI & Tools', slug: 'ai', icon: 'ai', appCount: '48K' },
  { name: 'Photo & Video', slug: 'photo-video', icon: 'photo-video', appCount: '210K' },
  { name: 'Music & Audio', slug: 'music', icon: 'music', appCount: '95K' },
  { name: 'Games', slug: 'games', icon: 'games', appCount: '580K' },
  { name: 'Social', slug: 'social', icon: 'social', appCount: '115K' },
  { name: 'Entertainment', slug: 'entertainment', icon: 'entertainment', appCount: '175K' },
  { name: 'Finance', slug: 'finance', icon: 'finance', appCount: '86K' },
  { name: 'Health & Fitness', slug: 'health', icon: 'health', appCount: '130K' },
  { name: 'Shopping', slug: 'shopping', icon: 'shopping', appCount: '92K' },
  { name: 'Travel', slug: 'travel', icon: 'travel', appCount: '64K' }
];

export const COUNTRIES_LIST = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' }
];
